"use client";

import { Button } from "@repo/ui/components/ui/button";
import { Separator } from "@repo/ui/components/ui/separator";
import { useFormatter, useTranslations } from "next-intl";
import Image from "next/image";
import { cn } from "@repo/ui/lib/utils";
import { Offers } from "@/types/offers";
import { currencyFormatter } from "@repo/next-shared/utils";
import DifferencePrice from "@repo/next-shared/components/difference-price";

type OfferDetailsProps = {
  offer: Offers;
  onReject: () => void;
  onAccept: () => void;
};

export default function OfferDetails({
  offer,
  onReject,
  onAccept,
}: OfferDetailsProps) {
  const t = useTranslations();
  const format = useFormatter();

  const formatOfferDate = (dateString: string) => {
    return format.dateTime(new Date(dateString), {
      weekday: "short",
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  const formatOfferTime = (dateString: string) => {
    return format.dateTime(new Date(dateString), {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <div className="relative pb-32">
      <div className="flex flex-col">
        <div className="w-full">
          <p className="text-xl sm:text-3xl text-secondary-black font-semibold mb-2">
            {t("offers.offer_info")}{" "}
            <span className="uppercase">{offer.id}</span>
          </p>
          <div
            className={cn(
              "inline-flex py-1 px-4 border rounded-[20px] h-auto mb-4 md:mb-10",
              "border-[#0047FF] bg-[#E6EDFF] text-[#0047FF]",
            )}
          >
            <span className="whitespace-normal break-words">
              {t("offers.offers_list_info")}
            </span>
          </div>
        </div>

        <div className={cn("flex items-start gap-6 flex-col md:flex-row")}>
          <div
            className={cn(
              "relative w-full md:w-[200px] h-[200px]",
              "overflow-hidden rounded-lg shadow-md",
            )}
          >
            <Image
              src={offer.catalogImgUrl || ""}
              alt={offer.productTitle || "product image"}
              width={200}
              height={200}
              className="object-contain w-full h-full"
            />
          </div>

          <div className="w-full md:w-auto flex-1">
            <h2 className={cn("text-2xl font-semibold text-secondary-black")}>
              {offer.productTitle}
            </h2>
            <p className="text-secondary-black mt-5 mb-2">{offer.brand}</p>
            <p className="text-secondary-black mb-5">
              {`${offer.size} | ${offer.color || ""} | ${offer.ean}`}
            </p>
          </div>
        </div>

        <p className="text-2xl font-semibold text-secondary-black mt-4 md:mt-9 mb-3">
          {t("offers.offers_details")}
        </p>
        <Separator />

        <div className="grid grid-cols-1 md:grid-cols-6 pt-4 pb-4">
          <div className="flex flex-col">
            <span className="font-bold text-lg text-secondary-black">
              {t("offers.fields.offerDate.offer_date_time")}
            </span>
            <span className="text-secondary-gray4 dark:text-secondary-gray1 capitalize">
              {formatOfferDate(offer.offerDate)}
            </span>
            <span className="text-secondary-gray4 dark:text-secondary-gray1">
              {t("offers.hours")} {formatOfferTime(offer.offerDate)}
            </span>
          </div>
          <div className="flex flex-col">
            <span className="font-bold text-lg text-secondary-black">
              {t("offers.fields.expiryDate.expiry_date_time")}
            </span>
            <span className="text-secondary-gray4 dark:text-secondary-gray1 capitalize">
              {formatOfferDate(offer.expiryDate)}
            </span>
            <span className="text-secondary-gray4 dark:text-secondary-gray1">
              {t("offers.hours")} {formatOfferTime(offer.expiryDate)}
            </span>
          </div>
          <div className="flex flex-col">
            <span className="font-bold text-lg text-secondary-black">
              {t("offers.fields.quantity.label")}
            </span>
            <span className="text-secondary-gray4 dark:text-secondary-gray1">
              {offer.stockQuantity}
            </span>
          </div>
          <div className="flex flex-col pr-10">
            <span className="font-bold text-lg text-secondary-black">
              {t("offers.fields.offerPrice.label1")}
            </span>
            <span className="font-bold text-[10px] text-secondary-black">
              {t("offers.fields.offerPrice.label2")}
            </span>
            <span className="text-secondary-gray4 dark:text-secondary-gray1">
              {currencyFormatter().format(offer.offerPrice)}
            </span>
          </div>
          <div className="flex flex-col">
            <span className="font-bold text-lg text-secondary-black">
              {t("offers.fields.sellingPrice.label")}
            </span>
            <span className="text-secondary-gray4 dark:text-secondary-gray1">
              {currencyFormatter().format(offer.sellingPrice)}
            </span>
          </div>
          <div className="flex flex-col">
            <span className="font-bold text-lg text-secondary-black">
              {t("offers.fields.delta.label")}
            </span>
            <DifferencePrice
              offerPrice={offer.offerPrice}
              sellingPrice={offer.sellingPrice}
            />
          </div>
        </div>

        <Separator />
      </div>
      <div
        className={cn(
          "fixed bottom-0 left-0 w-full bg-white drop-shadow-header",
          "p-5 md:px-10",
          "flex flex-col md:flex-row justify-between items-center",
          "space-y-4 md:space-y-0 md:space-x-4",
        )}
      >
        <Button variant="outline" onClick={onReject} className="w-full">
          {t("offers.cta_reject")}
        </Button>
        <Button onClick={onAccept} className="w-full">
          {t("offers.cta_accept")}
        </Button>
      </div>
    </div>
  );
}
